import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { Checkbox } from "@/components/ui/checkbox";
import SignatureCapture from "@/components/SignatureCapture";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

const DEPARTMENTS = ["Ön Büro", "Kat Hizmetleri", "Yiyecek & İçecek", "Mutfak", "Teknik Servis", "Güvenlik", "Muhasebe", "İK", "Kurumsal İletişim", "Satın Alma", "Yönetim"];
const COMMON_POSITIONS = ["Resepsiyonist", "Kat Görevlisi", "Garson", "Aşçı", "Sous Chef", "Teknisyen", "Güvenlik Görevlisi", "Muhasebe Uzmanı", "İK Uzmanı", "Departman Müdürü", "Gıda Mühendisi", "Hemşire", "Doktor", "Diğer"];

function passwordChecks(pw) {
  const checks = {
    length: pw.length >= 8,
    upper: /[A-ZĞÜŞİÖÇ]/.test(pw),
    lower: /[a-zğüşıöç]/.test(pw),
    number: /\d/.test(pw),
    noRepeat: !/(.)\1\1/.test(pw),
    noSequence: true,
  };
  const pwl = pw.toLowerCase();
  for (let i = 0; i < pwl.length - 2; i++) {
    const a = pwl.charCodeAt(i), b = pwl.charCodeAt(i + 1), c = pwl.charCodeAt(i + 2);
    if (((b - a === 1 && c - b === 1) || (a - b === 1 && b - c === 1)) && /[a-z0-9]/.test(pwl[i]) && /[a-z0-9]/.test(pwl[i+1]) && /[a-z0-9]/.test(pwl[i+2])) {
      checks.noSequence = false; break;
    }
  }
  return checks;
}

export default function Register() {
  const { register } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    email: "", password: "", password_confirm: "", first_name: "", last_name: "", phone: "",
    department: "Ön Büro", position: "", birth_date: "", role: "staff",
    signature: "", tc_kimlik: "", kvkk_consent: false, salary: "", gender: "",
  });
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const setF = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const pwChecks = passwordChecks(form.password);
  const pwOk = Object.values(pwChecks).every(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.signature) { toast.error("Lütfen imzanızı ekleyip Onayla'ya basın"); return; }
    if (!form.kvkk_consent) { toast.error("KVKK sözleşmesini kabul etmelisiniz"); return; }
    if (!pwOk) { toast.error("Şifre güvenlik kurallarına uymuyor"); return; }
    if (form.password !== form.password_confirm) {
      toast.error("Şifreler uyuşmuyor. Lütfen aynı şifreyi tekrar girin.");
      return;
    }
    setLoading(true);
    const res = await register(form);
    setLoading(false);
    if (!res.ok) return toast.error(res.error);
    toast.success(res.message || "Kaydınız alındı");
    nav("/login");
  };

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Colossae Personel</div>
          <h1 className="font-display text-3xl font-bold mt-1">Kayıt ol</h1>
          <div className="text-sm text-muted-foreground mt-1">Bilgilerinizi doldurun ve imzanızı ekleyin. Adminin onayı gereklidir.</div>
        </div>

        <Card>
          <CardContent className="p-6 md:p-8">
            <form onSubmit={submit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div><Label>Ad</Label><Input required value={form.first_name} onChange={setF("first_name")} data-testid="reg-first-name" /></div>
                <div><Label>Soyad</Label><Input required value={form.last_name} onChange={setF("last_name")} data-testid="reg-last-name" /></div>
                <div><Label>E-posta</Label><Input type="email" required value={form.email} onChange={setF("email")} data-testid="reg-email" /></div>
                <div>
                  <Label>Şifre</Label>
                  <Input type="password" required minLength={8} value={form.password} onChange={setF("password")} data-testid="reg-password" />
                  <div className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
                    En az <b>8 karakter</b>, <b>1 büyük harf</b>, <b>1 küçük harf</b>, <b>1 rakam</b> içermeli.
                    Aynı karakter üst üste 3 defa (aaa, 111) veya ardışık dizi (123, abc) içeremez.
                  </div>
                </div>
                <div>
                  <Label>Şifre (Tekrar)</Label>
                  <Input
                    type="password"
                    required minLength={8}
                    value={form.password_confirm}
                    onChange={setF("password_confirm")}
                    data-testid="reg-password-confirm"
                    className={form.password_confirm && form.password_confirm !== form.password ? "border-destructive" : ""}
                  />
                  {form.password_confirm && form.password_confirm !== form.password && (
                    <div className="text-[11px] text-destructive mt-1" data-testid="reg-password-mismatch">Şifreler uyuşmuyor</div>
                  )}
                  {form.password_confirm && form.password_confirm === form.password && form.password.length >= 8 && (
                    <div className="text-[11px] text-emerald-600 mt-1">✓ Şifreler eşleşiyor</div>
                  )}
                </div>
                <div><Label>Telefon</Label><Input required value={form.phone} onChange={setF("phone")} data-testid="reg-phone" /></div>
                <div><Label>T.C. Kimlik</Label><Input value={form.tc_kimlik} onChange={setF("tc_kimlik")} data-testid="reg-tc" /></div>
                <div>
                  <Label>Departman</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({...form, department: v})}>
                    <SelectTrigger data-testid="reg-department"><SelectValue /></SelectTrigger>
                    <SelectContent>{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Görev</Label>
                  <Select value={COMMON_POSITIONS.includes(form.position) ? form.position : (form.position ? "Diğer" : "")} onValueChange={(v) => setForm({...form, position: v === "Diğer" ? (COMMON_POSITIONS.includes(form.position) ? "" : form.position) : v})}>
                    <SelectTrigger data-testid="reg-position-select"><SelectValue placeholder="Görev seçin" /></SelectTrigger>
                    <SelectContent>{COMMON_POSITIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                  {(!COMMON_POSITIONS.includes(form.position) || form.position === "Diğer") && (
                    <Input className="mt-2" placeholder="Görev adını yazın" required value={form.position} onChange={setF("position")} data-testid="reg-position" />
                  )}
                </div>
                <div><Label>Maaşınız (₺)</Label><Input type="number" value={form.salary} onChange={setF("salary")} data-testid="reg-salary" placeholder="Örn: 30000" /></div>
                <div><Label>Doğum Tarihi</Label><Input type="date" required value={form.birth_date} onChange={setF("birth_date")} data-testid="reg-birthdate" /></div>
                <div>
                  <Label>Cinsiyet</Label>
                  <Select value={form.gender} onValueChange={(v) => setForm({...form, gender: v})}>
                    <SelectTrigger data-testid="reg-gender"><SelectValue placeholder="Seçiniz" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M">Erkek</SelectItem>
                      <SelectItem value="F">Kadın</SelectItem>
                      <SelectItem value="O">Belirtmek istemiyorum</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Rolünüz</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                    <SelectTrigger data-testid="reg-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Personel</SelectItem>
                      <SelectItem value="manager">Müdür</SelectItem>
                      <SelectItem value="gm">Genel Müdür</SelectItem>
                      <SelectItem value="hr">İK Sorumlusu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.password && (
                <div className="rounded-md border border-border bg-muted/30 p-3 space-y-1" data-testid="pw-strength">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("reg.pw_strength")}</div>
                  {[
                    { key: "length", label: t("reg.pw_length") },
                    { key: "upper", label: t("reg.pw_upper") },
                    { key: "lower", label: t("reg.pw_lower") },
                    { key: "number", label: t("reg.pw_number") },
                    { key: "noRepeat", label: t("reg.pw_norepeat") },
                    { key: "noSequence", label: t("reg.pw_nosequence") },
                  ].map((r) => (
                    <div key={r.key} className={`text-xs flex items-center gap-2 ${pwChecks[r.key] ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`}>
                      <span className={`w-3.5 h-3.5 rounded-full inline-flex items-center justify-center text-[9px] ${pwChecks[r.key] ? "bg-emerald-500 text-white" : "bg-muted-foreground/30 text-transparent"}`}>✓</span>
                      {r.label}
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-2">
                <Label>İmza (telefondan/mouse ile çiziniz)</Label>
                <SignatureCapture onChange={(sig) => setForm((f) => ({...f, signature: sig}))} testId="reg-signature" />
                {form.signature ? <div className="text-xs text-success mt-1">✓ İmza alındı</div> : <div className="text-xs text-muted-foreground mt-1">İmza sonrası "Onayla" düğmesine basın.</div>}
              </div>

              <div className="pt-2 border-t border-border pt-4">
                <div className="text-sm bg-secondary p-3 rounded max-h-40 overflow-y-auto text-xs">
                  <div className="font-semibold mb-1">KVKK Aydınlatma Metni</div>
                  <p>6698 sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") uyarınca, Colossae Hotel tarafından personel yönetimi kapsamında; ad, soyad, T.C. kimlik numarası, iletişim bilgileri, adres, doğum tarihi, fotoğraf, imza, banka bilgileri ve özlük bilgileri gibi kişisel verileriniz iş sözleşmesinin ifası, kanuni yükümlülüklerimizin yerine getirilmesi ve iş güvenliği amaçlarıyla işlenmektedir. Verileriniz yalnızca yetkili personel tarafından güvenli koşullarda işlenir, yasal süreler boyunca saklanır ve KVKK'nın 8. ve 9. maddelerine uygun şartlar dışında üçüncü kişilerle paylaşılmaz. Kanunun 11. maddesi kapsamındaki haklarınızı (bilgi talep etme, düzeltilme, silinme vb.) admin@colossae.com adresine yazılı başvuru ile kullanabilirsiniz.</p>
                </div>
                <div className="flex items-start gap-2 mt-2">
                  <Checkbox id="kvkk" checked={form.kvkk_consent} onCheckedChange={(v) => setForm({...form, kvkk_consent: !!v})} data-testid="reg-kvkk" className="mt-0.5" />
                  <Label htmlFor="kvkk" className="cursor-pointer text-sm">
                    <Link to="/privacy" target="_blank" className="text-primary underline" data-testid="reg-kvkk-link">KVKK Aydınlatma Metni</Link>'ni okudum ve kişisel verilerimin belirtilen amaçlarla işlenmesini kabul ediyorum. Ayrıca <Link to="/terms" target="_blank" className="text-primary underline" data-testid="reg-terms-link">Kullanım Şartları</Link>'nı kabul ediyorum.
                  </Label>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Link to="/login" className="text-sm text-primary underline" data-testid="link-back-login">Girişe dön</Link>
                <Button type="submit" disabled={loading} data-testid="reg-submit">{loading ? "Gönderiliyor…" : "Kayıt talebi gönder"}</Button>
              </div>
            </form>
          </CardContent>
        </Card>
        <div className="mt-6 flex justify-center gap-4 text-[11px] text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground underline decoration-dotted" data-testid="register-privacy">Gizlilik Politikası</Link>
          <Link to="/terms" className="hover:text-foreground underline decoration-dotted" data-testid="register-terms">Kullanım Şartları</Link>
        </div>
      </div>
    </div>
  );
}
